import { useEffect, useRef, useState } from "react";
import { LineChart } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  formatPrice,
  formatShortDate,
  getCollectedDaysLabel,
} from "@/lib/price";
import type { PriceSnapshot } from "@/mocks/products";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type ChartPoint = {
  capturedAt: string;
  date: string;
  price: number;
};

type ChartTooltipPayload = {
  payload: ChartPoint;
  value: number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<ChartTooltipPayload>;
};

function ChartTooltip({ active, payload }: ChartTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0].payload;

  return (
    <div className="rounded-md border border-[#273957] bg-[#071120] px-3 py-2 text-sm shadow-[0_18px_40px_rgba(0,0,0,0.35)]">
      <p className="font-bold text-white">{formatShortDate(point.capturedAt)}</p>
      <p className="font-extrabold tabular-nums text-[#ff4a25]">
        {formatPrice(point.price)}
      </p>
    </div>
  );
}

const formatAxisPrice = (value: number): string => {
  const millions = value / 1_000_000;
  return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
};

export function PriceHistoryChart({
  snapshots,
}: {
  snapshots: PriceSnapshot[];
}): JSX.Element {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const chartData = snapshots.map((snapshot) => ({
    capturedAt: snapshot.capturedAt,
    date: formatShortDate(snapshot.capturedAt),
    price: snapshot.price,
  }));
  const prices = chartData.map((point) => point.price);
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
  const priceSpan = Math.max(100000, maxPrice - minPrice);
  const yDomain: [number, number] = [
    Math.max(0, Math.floor((minPrice - priceSpan * 0.18) / 100000) * 100000),
    Math.ceil((maxPrice + priceSpan * 0.18) / 100000) * 100000,
  ];
  const chartHeight = 135;
  const firstDate = snapshots[0] ? formatShortDate(snapshots[0].capturedAt) : "Pending";
  const lastSnapshot = snapshots[snapshots.length - 1];
  const lastDate = lastSnapshot ? formatShortDate(lastSnapshot.capturedAt) : "Pending";

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) {
      return undefined;
    }

    const updateWidth = (width: number): void => {
      setChartWidth(Math.max(0, Math.floor(width)));
    };

    updateWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      updateWidth(entry?.contentRect.width ?? 0);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Card className="rounded-lg border-[#20304a] bg-[#071120] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <CardHeader className="grid gap-1 px-5 pb-2 pt-5">
        <div className="flex flex-row items-start justify-between gap-3">
          <CardTitle className="text-lg font-extrabold text-white">
            30-day price history
          </CardTitle>
          <span className="shrink-0 text-right text-sm font-medium text-[#9ca8ba]">
            {getCollectedDaysLabel(snapshots)}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm">
          <span className="text-[#9ca8ba]">
            {firstDate} - {lastDate}
          </span>
          {lastSnapshot ? (
            <strong className="font-extrabold tabular-nums text-white">
              {lastDate} / {formatPrice(lastSnapshot.price)}
            </strong>
          ) : null}
          <span />
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-0">
        {chartData.length < 2 ? (
          <div className="grid min-h-[250px] place-items-center rounded-lg border border-dashed border-[#263853] bg-[#050d18] text-center text-[#8f9bb0]">
            <div className="grid justify-items-center gap-2">
              <LineChart className="h-8 w-8" />
              <strong className="text-white">No collected history yet</strong>
              <span className="text-sm">First snapshot is pending.</span>
            </div>
          </div>
        ) : (
          <div className="h-[135px] min-w-0" ref={chartContainerRef}>
            {chartWidth > 0 ? (
              <AreaChart
                data={chartData}
                height={chartHeight}
                margin={{ bottom: 8, left: 4, right: 12, top: 12 }}
                width={chartWidth}
              >
                <defs>
                  <linearGradient id="priceFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#ff4a25" stopOpacity={0.42} />
                    <stop offset="95%" stopColor="#ff4a25" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="#253650"
                  strokeDasharray="4 5"
                  vertical={false}
                />
                <XAxis
                  axisLine={false}
                  dataKey="date"
                  interval="preserveStartEnd"
                  minTickGap={22}
                  tick={{ fill: "#a3aec1", fontSize: 12, fontWeight: 700 }}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  domain={yDomain}
                  tick={{ fill: "#a3aec1", fontSize: 12, fontWeight: 700 }}
                  tickFormatter={(value: number) => formatAxisPrice(value)}
                  tickLine={false}
                  width={52}
                />
                <RechartsTooltip
                  content={(props) => (
                    <ChartTooltip
                      active={props.active}
                      payload={
                        props.payload as
                          | ReadonlyArray<ChartTooltipPayload>
                          | undefined
                      }
                    />
                  )}
                  cursor={{ stroke: "rgba(255,255,255,0.2)", strokeDasharray: "4 4" }}
                />
                <Area
                  activeDot={{ fill: "#ff4a25", r: 6, stroke: "#7a210f", strokeWidth: 4 }}
                  dataKey="price"
                  dot={{ fill: "#071120", r: 2.8, stroke: "#ff4a25", strokeWidth: 2 }}
                  fill="url(#priceFill)"
                  isAnimationActive={false}
                  stroke="#ff4a25"
                  strokeWidth={3.5}
                  type="monotone"
                />
              </AreaChart>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
